import { StyleSheet, css } from 'aphrodite'
import { useEffect, useState } from 'react'
import { userService } from '../../services/userService'
import type { Contact, User } from '../../types/user'
import Modal from '../Modal'
import MultiSelect, { type Option } from '../MultiSelect'

interface Tag {
  id: number
  label?: string
  status?: string
  title?: string | null
}

interface StatusReportConfirmationModalProps {
  isOpen: boolean
  completedTags: Tag[]
  onClose: () => void
  onConfirm: (recipientUserIds: number[], notes: string) => Promise<void>
  isSubmitting?: boolean
  suggestedRecipientIds?: number[]
}

const StatusReportConfirmationModal: React.FC<
  StatusReportConfirmationModalProps
> = ({
  isOpen,
  completedTags,
  onClose,
  onConfirm,
  isSubmitting = false,
  suggestedRecipientIds = [],
}) => {
  const [contacts, setContacts] = useState<Contact[]>([])
  const [selectedRecipients, setSelectedRecipients] = useState<Option[]>([])
  const [isLoadingContacts, setIsLoadingContacts] = useState(false)
  const [notes, setNotes] = useState('')

  useEffect(() => {
    loadContacts()
  }, [])

  // Auto-select suggested recipients (e.g. contractor) once contacts load
  useEffect(() => {
    if (
      suggestedRecipientIds.length > 0 &&
      contacts.length > 0 &&
      selectedRecipients.length === 0
    ) {
      const suggestedOptions = contactOptions.filter((opt) =>
        suggestedRecipientIds.includes(Number.parseInt(opt.value)),
      )
      if (suggestedOptions.length > 0) {
        setSelectedRecipients(suggestedOptions)
      }
    }
  }, [contacts, suggestedRecipientIds, selectedRecipients.length])

  const loadContacts = async () => {
    setIsLoadingContacts(true)
    try {
      const contactsData = await userService.getContacts()
      setContacts(contactsData)
    } catch (error) {
      console.error('Failed to load contacts:', error)
    } finally {
      setIsLoadingContacts(false)
    }
  }

  const handleConfirm = async () => {
    const recipientUserIds = selectedRecipients.map((option) =>
      Number.parseInt(option.value),
    )
    await onConfirm(recipientUserIds, notes)
  }

  const contactOptions: Option[] = contacts
    .filter((c) => {
      const userId = c.contact_object?.user_id || c.contact_object?.id
      return userId
    })
    .map((c) => {
      const userId = c.contact_object.user_id || c.contact_object.id
      const companyName = c.contact_object.company_name || 'No Company'
      const userType = c.contact_object.user_type || 'Unknown'
      const displayName = c.contact_object.name || c.contact_object.email

      return {
        value: String(userId),
        label: displayName,
        name: displayName,
        email: c.contact_object.email,
        companyName: companyName,
        userType: userType,
      } as Option
    })

  return (
    <Modal onClose={onClose}>
      <div className={css(styles.confirmationModal)}>
        <div className={css(styles.confirmationHeader)}>
          <div className={css(styles.confirmationHeaderIcon)}>
            <i className="fa-solid fa-chart-line" />
          </div>
          <h2 className={css(styles.confirmationHeaderTitle)}>
            Generate Status Report
          </h2>
        </div>
        <div className={css(styles.confirmationContent)}>
          <p className={css(styles.confirmationText)}>
            Generating status report from the following completed tags:
          </p>
          <div className={css(styles.tagsList)}>
            {completedTags.map((tag, index) => (
              <div key={tag.id || index} className={css(styles.tagItem)}>
                <div className={css(styles.tagInfo)}>
                  <span className={css(styles.tagTitle)}>
                    {tag.title || 'Untitled Tag'}
                  </span>
                  <span className={css(styles.tagLabel)}>{tag.label}</span>
                </div>
                <div className={css(styles.tagStatus)}>
                  <span className={css(styles.statusBadge, styles.complete)}>
                    {tag.status}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className={css(styles.recipientsSection)}>
            <label className={css(styles.recipientsLabel)}>
              Send Report To (Optional)
            </label>
            <p className={css(styles.recipientsDescription)}>
              Select contacts to send this status report to. Leave empty to
              generate report for personal use.
            </p>
            <MultiSelect
              isMulti={true}
              placeholder={
                isLoadingContacts ? 'Loading contacts...' : 'Select recipients'
              }
              options={contactOptions}
              value={selectedRecipients}
              onChange={(options) => setSelectedRecipients(options as Option[])}
              isDisabled={isLoadingContacts}
            />
          </div>

          <div className={css(styles.notesSection)}>
            <label className={css(styles.recipientsLabel)}>
              Notes (Optional)
            </label>
            <textarea
              className={css(styles.notesTextarea)}
              placeholder="Add notes to include with this status report..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <div className={css(styles.confirmationActions)}>
          <button
            onClick={onClose}
            className={css(styles.cancelButton)}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className={css(styles.proceedButton)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" /> Generating...
              </>
            ) : (
              'Generate Report'
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}

const styles = StyleSheet.create({
  confirmationModal: {
    padding: '32px',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    boxShadow:
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    margin: '0 auto',
    maxWidth: '600px',
    width: '90vw',
  },
  confirmationHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '20px',
    color: '#1C64F2',
  },
  confirmationHeaderIcon: {
    fontSize: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    backgroundColor: '#EBF4FF',
    borderRadius: '50%',
  },
  confirmationHeaderTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  confirmationContent: {
    marginBottom: '32px',
  },
  confirmationText: {
    fontSize: '16px',
    color: '#4B5563',
    lineHeight: '1.6',
    marginBottom: '20px',
    textAlign: 'center',
  },
  tagsList: {
    maxHeight: '200px',
    overflowY: 'auto',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    backgroundColor: '#F9FAFB',
    marginBottom: '24px',
  },
  tagItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #E5E7EB',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  tagInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  tagTitle: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#111827',
  },
  tagLabel: {
    fontSize: '12px',
    color: '#6B7280',
  },
  tagStatus: {
    display: 'flex',
    alignItems: 'center',
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'uppercase',
  },
  complete: {
    backgroundColor: '#D1FAE5',
    color: '#065F46',
  },
  recipientsSection: {
    marginTop: '20px',
  },
  recipientsLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '8px',
  },
  recipientsDescription: {
    fontSize: '12px',
    color: '#6B7280',
    marginBottom: '12px',
    lineHeight: '1.4',
  },
  notesSection: {
    marginTop: '20px',
  },
  notesTextarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#111827',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
    lineHeight: '1.5',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 2px rgba(28, 100, 242, 0.2)',
    },
  },
  confirmationActions: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'space-between',
  },
  cancelButton: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: '#E5E7EB',
    },
  },
  proceedButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#1D4ED8',
    },
  },
})

export default StatusReportConfirmationModal
